/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDeselectRecord';

const ActionDeselectRecordManager: ActionManagerFn<'ActionDeselectRecord'> = (
  platform: Platform,
): ActionManager<'ActionDeselectRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionDeselectRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'ReadOnlyDataStore',
        ],
      },
      recordIdExpression: {
        label: 'Record ID',
        type: 'expressionOnly',
        returnType: () => 'string',
        required: true,
      },
    },
    name: 'Deselect Record',
    getDisplayText: (item) => item.props.alias,
    icon: { iconName: 'square' },
    description: `**Overview**<br/>
The Deselect Record action is a precision UI state management component designed to remove the selection state from a single, specific record within a Data Store. Unlike "Deselect All," which clears everything, this action targets a unique record identifier to toggle it off while leaving other selections intact.<br/><br/>
This component requires two key pieces of information: the target Data Store and the specific ID of the record to be deselected. It supports dynamic ID resolution via expressions, enabling logic-driven updates to selection state (for example, deselecting a row after a condition is met).

**Key Features**<br/>
* **Targeted Deselection:** Operates on a specific record ID, allowing granular control over the selection set without affecting other selected items.
* **Dynamic Identification:** Uses an expressionOnly property (recordIdExpression) to identify the record, enabling variables/runtime data to determine which item to deselect.
* **Broad Store Compatibility:** Supports CloudDataStore, DataStore, LocalDataStore, and ReadOnlyDataStore.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store containing the record.
* **Record ID (recordIdExpression) (Required):** An expressionOnly property defining the record identifier.
  * *Type:* Expression returning a string.
  * *Function:* Must evaluate to the unique identifier (ID) of the record to be deselected.

**Use Cases**<br/>
* **Conditional Removal:** Automatically deselecting a record if it becomes ineligible for a batch operation due to edits or validation.
* **Toggle Logic:** Implementing custom toggle behavior where clicking an already-selected item triggers this action to deselect it.`,
  };
};

export default ActionDeselectRecordManager;
